import { Add, ArchiveOutlined, Delete, Edit, Search, TrendingUp, UnarchiveOutlined, WarningAmberOutlined } from "@mui/icons-material";
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import { format, startOfMonth } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import PrinterFilamentSlots from '../../components/printers/PrinterFilamentSlots';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import JobListSpecs from '../../components/jobs/JobListSpecs';
import PrinterSnapshotThumb from '../../components/printers/PrinterSnapshotThumb';
import PrinterPrintProgressBar, {
    resolvePrinterPrintProgressPercent,
} from '../../components/printers/PrinterPrintProgressBar';
import PrinterRuntimeStatus, { idleCardSx } from '../../components/printers/PrinterRuntimeStatus';
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import ViewModeToggle from '../../components/ViewModeToggle';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatDate, presentUuid } from '../../helpers/formatters';
import {
    JOBS_PRINTER_STATS_VIEW_KEY,
    readViewMode,
} from '../../helpers/viewModePreference';
import { useListFetch } from '../../hooks/useListFetch';
import { useReloadOnPageRestore } from '../../hooks/useReloadOnPageRestore';
import { useTickingNow } from '../../hooks/useTickingNow';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import { filterActivePrinters, sortPrintersByRegistrationOrder } from '../../helpers/printers';
import { applyPrinterRuntimeFlags, loadIdlePrinterMap, patchActivityMapForNotification } from '../../helpers/idlePrinters';
import { openRestrictionForPrinter } from '../../helpers/printerProductivity';
import { printersInProgressSet } from '../../helpers/printersOverview';
import {
    buildJobsListQuery,
    buildPrinterStatsQuery,
    buildPrinterSuccessCards,
    buildSharedJobsQueryParams,
    currentMonthJobsDateRange,
    formatFilamentGrams,
    formatProductionDurationMinutes,
    getJobStatusBadgeProps,
    hasActiveJobsQueryFilters,
    jobsDateRangeForApi,
    JOB_FILAMENT_PENDING_FILTER,
    JOB_LIST_STATUS_FILTERS,
    normalizeJobsDateRange,
    PRINTER_STATS_PERIOD_MODES,
    resolvePrinterStatsQueryParams,
    successRateFromTotals,
    summarizePrinterStatsProduction,
} from '../../helpers/jobs';
import {
    fetchJobDetails,
    mergeJobInList,
    patchPrinterStatsForNotification,
    patchPrintersInProgressSet,
} from '../../helpers/jobLivePatch';
import { seedJobLiveInProgress } from '../../helpers/jobLiveInProgress';
import { isJobIntegrationLiveNotification, LIVE_NOTIFICATION_KINDS, subscribeLiveNotifications } from '../../helpers/liveNotifications';
import {
    clearPrinterPrintProgress,
    loadPrinterPrintProgressMap,
} from '../../helpers/printerPrintProgressStorage';
import { liveUpdateHighlightSx, useLiveUpdateHighlights } from '../../helpers/liveUpdateHighlight';

function PrinterSuccessStatRow({ printerCard, highlighted }) {
    const brandBadge = getPrinterBrandBadgeProps(printerCard.brand);
    const successRate = Number(printerCard.success_rate) || 0;
    return (
        <TableRow hover sx={liveUpdateHighlightSx(highlighted)}>
            <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <PrinterSnapshotThumb
                            printer={printerCard}
                            size="row"
                            alt={`Câmera de ${printerCard.name || 'impressora'}`}
                        />
                        {brandBadge ? (
                            <Chip label={printerCard.name} size="small" {...brandBadge} />
                        ) : (
                            <Typography variant="body2" fontWeight={700} noWrap>
                                {printerCard.name}
                            </Typography>
                        )}
                    </Box>
                    <PrinterFilamentSlots
                        printerUuid={printerCard.printer_uuid}
                        printerName={printerCard.name}
                        filaments={printerCard.filaments}
                        restriction={printerCard.productivity_restriction}
                    />
                </Box>
            </TableCell>
            <TableCell>
                <PrinterRuntimeStatus
                    isPrinting={printerCard.is_printing}
                    isIdle={printerCard.is_idle}
                    idleLabel={printerCard.idle_label}
                    lastActivityAt={printerCard.last_activity_at}
                    restriction={printerCard.productivity_restriction}
                />
            </TableCell>
            <TableCell>
                <Typography variant="body1" fontWeight={800} component="span">
                    {successRate.toFixed(1).replace('.', ',')}%
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    {printerCard.total_printed}/{printerCard.total_objects} objetos impressos
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography variant="body2" fontWeight={600}>
                    {printerCard.total_jobs}
                </Typography>
            </TableCell>
            <TableCell>
                <Typography variant="body2">
                    {formatProductionDurationMinutes(printerCard.total_duration_minutes)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    {formatFilamentGrams(printerCard.total_filament_grams)}
                </Typography>
            </TableCell>
        </TableRow>
    );
}

function PrinterSuccessStatCard({ printerCard, printerProgress, highlighted }) {
    const brandBadge = getPrinterBrandBadgeProps(printerCard.brand);
    const successRate = Number(printerCard.success_rate) || 0;
    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                ...idleCardSx(printerCard.is_printing, printerCard.is_idle, printerCard.productivity_restriction),
                ...liveUpdateHighlightSx(highlighted),
            }}
        >
            <PrinterSnapshotThumb
                printer={printerCard}
                alt={`Câmera de ${printerCard.name || 'impressora'}`}
            />
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1.5,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        {brandBadge ? (
                            <Chip label={printerCard.name} size="small" {...brandBadge} />
                        ) : (
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                                {printerCard.name}
                            </Typography>
                        )}
                    </Box>
                    <PrinterRuntimeStatus
                        isPrinting={printerCard.is_printing}
                        isIdle={printerCard.is_idle}
                        idleLabel={printerCard.idle_label}
                        lastActivityAt={printerCard.last_activity_at}
                        showRecentStopped={false}
                        restriction={printerCard.productivity_restriction}
                    />
                </Box>
                {printerCard.is_printing && (
                    <PrinterPrintProgressBar
                        percent={resolvePrinterPrintProgressPercent(
                            printerProgress,
                            printerCard.printer_uuid,
                        )}
                    />
                )}
                <Box sx={{ mb: 1.5 }}>
                    <PrinterFilamentSlots
                        printerUuid={printerCard.printer_uuid}
                        printerName={printerCard.name}
                        filaments={printerCard.filaments}
                        restriction={printerCard.productivity_restriction}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <TrendingUp fontSize="small" color="primary" />
                    <Typography variant="body2" color="text.secondary">
                        Taxa de sucesso
                    </Typography>
                </Box>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                    {successRate.toFixed(1).replace('.', ',')}%
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({printerCard.total_jobs} impressões)
                    </Typography>
                </Typography>
                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, successRate)}
                    sx={{ height: 6, borderRadius: 1, mb: 1 }}
                />
                <Typography variant="caption" color="text.secondary" display="block">
                    {printerCard.total_printed}/{printerCard.total_objects} objetos impressos
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {formatProductionDurationMinutes(printerCard.total_duration_minutes)}
                    {' · '}
                    {formatFilamentGrams(printerCard.total_filament_grams)}
                </Typography>
            </CardContent>
        </Card>
    );
}

function initialJobFiltersFromSearchParams(searchParams) {
    const month = currentMonthJobsDateRange();
    const fromParam = searchParams.get('finished_from') || '';
    const toParam = searchParams.get('finished_to') || '';
    const hasExplicitPeriod = Boolean(fromParam && toParam);
    const status = searchParams.get('status') || '';
    // Deep-link from the pending alert: only that filter, no default month range.
    const filamentPendingOnly = status === JOB_FILAMENT_PENDING_FILTER && !fromParam && !toParam;
    return {
        printer_uuid: searchParams.get('printer_uuid') || '',
        status,
        finished_from: filamentPendingOnly ? '' : (hasExplicitPeriod ? fromParam : month.from),
        finished_to: filamentPendingOnly ? '' : (hasExplicitPeriod ? toParam : month.to),
        q: (searchParams.get('q') || searchParams.get('search') || '').trim(),
    };
}

function ListJobsPage() {
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const initialFilters = useMemo(() => initialJobFiltersFromSearchParams(searchParams), [searchParams]);
    const defaultMonthRange = useMemo(() => currentMonthJobsDateRange(), []);
    const [jobs, setJobs] = useState([]);
    const [printers, setPrinters] = useState([]);
    const [printersById, setPrintersById] = useState({});
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalJobs, setTotalJobs] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [draftSearchTerm, setDraftSearchTerm] = useState(initialFilters.q);
    const [appliedSearchQuery, setAppliedSearchQuery] = useState(initialFilters.q);
    const [listVersion, setListVersion] = useState(0);
    const { isPrinterHighlighted, isJobHighlighted } = useLiveUpdateHighlights(isJobIntegrationLiveNotification);
    const [draftPrinter, setDraftPrinter] = useState(initialFilters.printer_uuid);
    const [draftStatus, setDraftStatus] = useState(initialFilters.status);
    const [draftDateFrom, setDraftDateFrom] = useState(initialFilters.finished_from);
    const [draftDateTo, setDraftDateTo] = useState(initialFilters.finished_to);
    const [queryFilters, setQueryFilters] = useState({
        printer_uuid: initialFilters.printer_uuid,
        status: initialFilters.status,
        finished_from: initialFilters.finished_from,
        finished_to: initialFilters.finished_to,
    });
    const [printerStats, setPrinterStats] = useState([]);
    const [printersInProgress, setPrintersInProgress] = useState(() => new Set());
    const [activityByPrinter, setActivityByPrinter] = useState(() => new Map());
    const [productivityRestrictions, setProductivityRestrictions] = useState([]);
    const [printerProgress, setPrinterProgress] = useState({});
    const idleLoadSeq = useRef(0);
    const defaultStatsPeriod = useMemo(() => {
        const today = new Date();
        return {
            from: format(startOfMonth(today), 'yyyy-MM-dd'),
            to: format(today, 'yyyy-MM-dd'),
        };
    }, []);
    const [statsPeriodMode, setStatsPeriodMode] = useState('all_time');
    const [statsViewMode, setStatsViewMode] = useState(() => readViewMode(JOBS_PRINTER_STATS_VIEW_KEY));
    const [statsCustomFrom, setStatsCustomFrom] = useState(defaultStatsPeriod.from);
    const [statsCustomTo, setStatsCustomTo] = useState(defaultStatsPeriod.to);
    const [statsLoading, setStatsLoading] = useState(false);
    const [showArchivedPrinters, setShowArchivedPrinters] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const filtersApplied = useMemo(() => {
        const monthOnlyDefault = (
            !queryFilters.printer_uuid
            && !queryFilters.status
            && !appliedSearchQuery
            && queryFilters.finished_from === defaultMonthRange.from
            && queryFilters.finished_to === defaultMonthRange.to
        );
        return hasActiveJobsQueryFilters(queryFilters, appliedSearchQuery) && !monthOnlyDefault;
    }, [queryFilters, appliedSearchQuery, defaultMonthRange]);

    const sharedQueryParams = useMemo(
        () => buildSharedJobsQueryParams(queryFilters, { q: appliedSearchQuery }),
        [queryFilters, appliedSearchQuery],
    );

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                const list = sortPrintersByRegistrationOrder(response.data.printers || []);
                setPrinters(list);
                const byId = {};
                list.forEach((p) => {
                    byId[p.id] = { name: p.name, brand: p.brand };
                });
                setPrintersById(byId);
            })
            .catch(() => {});
    }, []);

    const fetchPrinterStats = useCallback(() => {
        const resolved = resolvePrinterStatsQueryParams({
            mode: statsPeriodMode,
            customFrom: statsCustomFrom,
            customTo: statsCustomTo,
        });
        if (resolved.error) {
            Swal.fire('Período inválido', resolved.error, 'warning');
            return Promise.resolve();
        }

        const statsQuery = buildPrinterStatsQuery(resolved.params);

        setStatsLoading(true);
        const seq = ++idleLoadSeq.current;
        return Promise.all([
            api.get(`jobs/printer-stats?${statsQuery}`),
            api.get('jobs?status=em_andamento&limit=200'),
        ])
            .then(async ([statsResponse, inProgressResponse]) => {
                if (seq !== idleLoadSeq.current) {
                    return;
                }
                const inProgressJobs = inProgressResponse.data.data?.jobs || [];
                setPrinterStats(statsResponse.data.data?.printers || []);
                seedJobLiveInProgress(inProgressJobs);
                setPrintersInProgress(printersInProgressSet(inProgressJobs));

                if (!printers.length) {
                    return;
                }
                try {
                    const { activityMap, restrictions } = await loadIdlePrinterMap(api, printers, inProgressJobs);
                    if (seq !== idleLoadSeq.current) {
                        return;
                    }
                    setActivityByPrinter(activityMap);
                    setProductivityRestrictions(restrictions || []);
                } catch {
                    // Mantém o mapa anterior para não “apagar” a flag em falha transitória.
                }
            })
            .catch(() => {
                if (seq !== idleLoadSeq.current) {
                    return;
                }
                Swal.fire('Erro', 'Não foi possível carregar as taxas de sucesso.', 'error');
            })
            .finally(() => {
                if (seq === idleLoadSeq.current) {
                    setStatsLoading(false);
                }
            });
    }, [statsPeriodMode, statsCustomFrom, statsCustomTo, printers]);

    const fetchJobs = useCallback(() => {
        setIsLoading(true);
        const listQuery = buildJobsListQuery({
            page: currentPage,
            limit: itemsPerPage,
            ...sharedQueryParams,
        });

        return api.get(`jobs?${listQuery}`)
            .then((jobsResponse) => {
                const data = jobsResponse.data.data || {};
                setJobs(data.jobs || []);
                setTotalPages(data.total_pages || 1);
                setTotalJobs(data.total ?? (data.jobs || []).length);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, sharedQueryParams]);

    const bumpListFetch = useCallback(() => {
        setListVersion((v) => v + 1);
    }, []);

    useReloadOnPageRestore(bumpListFetch);

    useEffect(() => {
        const printerUuids = new Set([...printersInProgress]);
        jobs.forEach((job) => {
            if (job.status === 'em_andamento' && job.printer_uuid) {
                printerUuids.add(job.printer_uuid);
            }
        });
        setPrinterProgress((prev) => ({
            ...loadPrinterPrintProgressMap([...printerUuids]),
            ...prev,
        }));
    }, [printersInProgress, jobs]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (detail?.kind !== LIVE_NOTIFICATION_KINDS.PRINTER_SNAPSHOT_UPDATED) {
                return;
            }
            if (detail.progressPercent == null || !detail.printerUuid) {
                return;
            }
            setPrinterProgress((prev) => ({
                ...prev,
                [detail.printerUuid]: detail.progressPercent,
            }));
        });
    }, []);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }
            if (detail.action !== 'finished' || !detail.printerUuid) {
                return;
            }
            clearPrinterPrintProgress(detail.printerUuid);
            setPrinterProgress((prev) => {
                if (!prev[detail.printerUuid]) {
                    return prev;
                }
                const next = { ...prev };
                delete next[detail.printerUuid];
                return next;
            });
        });
    }, []);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }

            setPrintersInProgress((prev) => patchPrintersInProgressSet(prev, detail));
            setPrinterStats((prev) => patchPrinterStatsForNotification(prev, detail));
            if (detail.action === 'started' || detail.action === 'finished') {
                setActivityByPrinter((prev) => patchActivityMapForNotification(prev, detail, {
                    printerName: printersById[detail.printerUuid]?.name,
                }));
            }

            fetchJobDetails(detail.jobUuid).then((job) => {
                if (!job) {
                    return;
                }

                setJobs((prev) => mergeJobInList(prev, job, {
                    filters: {
                        ...queryFilters,
                        searchTerm: appliedSearchQuery,
                    },
                    currentPage,
                    pageSize: itemsPerPage,
                }));
            });
        });
    }, [queryFilters, appliedSearchQuery, currentPage, itemsPerPage, printersById]);

    useListFetch(fetchJobs, [
        location.key,
        currentPage,
        itemsPerPage,
        appliedSearchQuery,
        listVersion,
        fetchJobs,
    ]);

    useEffect(() => {
        if (statsPeriodMode !== 'custom') {
            fetchPrinterStats();
        }
    }, [statsPeriodMode, fetchPrinterStats]);

    const now = useTickingNow(15000);
    const printerSuccessCards = useMemo(
        () => applyPrinterRuntimeFlags(
            buildPrinterSuccessCards(printers, printerStats, printersInProgress),
            {
                printingSet: printersInProgress,
                activityMap: activityByPrinter,
                now,
            },
        ).map((card) => ({
            ...card,
            productivity_restriction: openRestrictionForPrinter(
                productivityRestrictions,
                card.printer_uuid || card.id,
            ),
        })),
        [printers, printerStats, printersInProgress, activityByPrinter, productivityRestrictions, now],
    );

    const activePrinterSuccessCards = useMemo(
        () => printerSuccessCards.filter((card) => !card.archived),
        [printerSuccessCards],
    );
    const archivedPrinterSuccessCards = useMemo(
        () => printerSuccessCards.filter((card) => card.archived),
        [printerSuccessCards],
    );

    const productionSummary = useMemo(
        () => summarizePrinterStatsProduction(printerStats),
        [printerStats],
    );

    const productionSuccessRate = useMemo(
        () => successRateFromTotals(productionSummary.total_printed, productionSummary.total_objects),
        [productionSummary],
    );

    const triggerReload = () => {
        setCurrentPage(1);
        setListVersion((v) => v + 1);
    };

    const handleApplyFilters = () => {
        const range = jobsDateRangeForApi(draftDateFrom, draftDateTo);
        if (range.error) {
            Swal.fire('Período inválido', range.error, 'warning');
            return;
        }
        const normalized = normalizeJobsDateRange(draftDateFrom, draftDateTo);
        setDraftDateFrom(normalized.start);
        setDraftDateTo(normalized.end);
        setQueryFilters({
            printer_uuid: draftPrinter,
            status: draftStatus,
            finished_from: range.finished_from || '',
            finished_to: range.finished_to || '',
        });
        setAppliedSearchQuery(String(draftSearchTerm || '').trim());
        triggerReload();
    };

    const handleClearFilters = () => {
        setDraftPrinter('');
        setDraftStatus('');
        setDraftDateFrom(defaultMonthRange.from);
        setDraftDateTo(defaultMonthRange.to);
        setDraftSearchTerm('');
        setAppliedSearchQuery('');
        setQueryFilters({
            printer_uuid: '',
            status: '',
            finished_from: defaultMonthRange.from,
            finished_to: defaultMonthRange.to,
        });
        triggerReload();
    };

    const handleFilamentPendingAlertClick = () => {
        setDraftPrinter('');
        setDraftStatus(JOB_FILAMENT_PENDING_FILTER);
        setDraftDateFrom('');
        setDraftDateTo('');
        setDraftSearchTerm('');
        setAppliedSearchQuery('');
        setQueryFilters({
            printer_uuid: '',
            status: JOB_FILAMENT_PENDING_FILTER,
            finished_from: '',
            finished_to: '',
        });
        navigate('/admin/jobs?status=filament_pending', { replace: true });
        triggerReload();
    };

    const handleStatsPeriodModeChange = (_event, value) => {
        if (value) {
            setStatsPeriodMode(value);
        }
    };

    const handleStatsCustomDateFromChange = (value) => {
        setStatsCustomFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(value, statsCustomTo || value);
        setStatsCustomFrom(normalized.start);
        setStatsCustomTo(normalized.end);
    };

    const handleStatsCustomDateToChange = (value) => {
        setStatsCustomTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(statsCustomFrom || value, value);
        setStatsCustomFrom(normalized.start);
        setStatsCustomTo(normalized.end);
    };

    const handleApplyStatsPeriod = () => {
        fetchPrinterStats();
    };

    const handleDraftDateFromChange = (value) => {
        setDraftDateFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(value, draftDateTo || value);
        setDraftDateFrom(normalized.start);
        setDraftDateTo(normalized.end);
    };

    const handleDraftDateToChange = (value) => {
        setDraftDateTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(draftDateFrom || value, value);
        setDraftDateFrom(normalized.start);
        setDraftDateTo(normalized.end);
    };

    const draftDateToMin = draftDateFrom || '';
    const draftDateFromMax = draftDateTo || '';

    const handleCreateJob = () => {
        navigate('/admin/register-job');
    };

    const handleEditJob = (id) => {
        navigate(`/admin/edit-job/${id}`);
    };

    const handleDeleteJob = (jobId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Esta impressão será removida permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
        }).then((result) => {
            if (result.isConfirmed && !loadingDelete) {
                setLoadingDelete(true);
                api.delete(`jobs/${jobId}`)
                    .then(() => {
                        setJobs((prev) => prev.filter((job) => job.id !== jobId));
                        Swal.fire('Removido!', 'A impressão foi removida com sucesso.', 'success');
                    })
                    .catch(() => {
                        Swal.fire('Erro!', 'Houve um problema ao remover a impressão.', 'error');
                    })
                    .finally(() => {
                        setLoadingDelete(false);
                    });
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Impressões</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: 2,
            }}>
                <Typography variant="h4" gutterBottom>
                    Impressões
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                    <PageTutorialButtons pageKey="jobs" fullWidth={isMobile} />
                    <QuotaLimitedButton
                        quotaKey="jobs"
                        variant="contained"
                        color="primary"
                        onClick={handleCreateJob}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Impressão
                    </QuotaLimitedButton>
                </Box>
            </Box>

            {printers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mb: 1.5,
                        }}
                    >
                        <Typography variant="subtitle1">
                            Taxa de sucesso por impressora
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: isMobile ? 'stretch' : 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <ViewModeToggle
                                value={statsViewMode}
                                onChange={setStatsViewMode}
                                storageKey={JOBS_PRINTER_STATS_VIEW_KEY}
                            />
                            {archivedPrinterSuccessCards.length > 0 && (
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                    startIcon={showArchivedPrinters ? <UnarchiveOutlined /> : <ArchiveOutlined />}
                                    onClick={() => setShowArchivedPrinters((prev) => !prev)}
                                >
                                    {showArchivedPrinters
                                        ? 'Ver ativas'
                                        : `Ver arquivadas (${archivedPrinterSuccessCards.length})`}
                                </Button>
                            )}
                            <ToggleButtonGroup
                                size="small"
                                color="primary"
                                exclusive
                                value={statsPeriodMode}
                                onChange={handleStatsPeriodModeChange}
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {PRINTER_STATS_PERIOD_MODES.map((option) => (
                                    <ToggleButton key={option.value} value={option.value}>
                                        {option.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>
                    </Box>

                    {statsPeriodMode === 'custom' && (
                        <Grid container spacing={2} sx={{ mb: 1.5 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data inicial"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={statsCustomFrom}
                                    onChange={(e) => handleStatsCustomDateFromChange(e.target.value)}
                                    inputProps={{ max: statsCustomTo || undefined }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    label="Data final"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={statsCustomTo}
                                    onChange={(e) => handleStatsCustomDateToChange(e.target.value)}
                                    inputProps={{ min: statsCustomFrom || undefined }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Button variant="contained" onClick={handleApplyStatsPeriod} disabled={statsLoading}>
                                    Atualizar período
                                </Button>
                            </Grid>
                        </Grid>
                    )}

                    {statsLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}

                    {!statsLoading && printerStats.length > 0 && (
                        <Card variant="outlined" sx={{ mb: 2, opacity: statsLoading ? 0.5 : 1 }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Tempo (estimado)
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {formatProductionDurationMinutes(productionSummary.total_duration_minutes)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Filamento (estimado)
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {formatFilamentGrams(productionSummary.total_filament_grams)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Sucesso em objetos
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {productionSuccessRate.toFixed(1).replace('.', ',')}%
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Impressões no período
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {productionSummary.total_jobs}
                                        </Typography>
                                    </Grid>
                                    {productionSummary.filament_pending_jobs > 0 && (
                                        <Grid item xs={12}>
                                            <Alert severity="warning" icon={<WarningAmberOutlined />} sx={{ py: 0.5 }}>
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={handleFilamentPendingAlertClick}
                                                    sx={{
                                                        all: 'unset',
                                                        cursor: 'pointer',
                                                        textDecoration: 'underline',
                                                        textUnderlineOffset: 2,
                                                        '&:hover': { opacity: 0.85 },
                                                    }}
                                                >
                                                    {productionSummary.filament_pending_jobs === 1
                                                        ? '1 impressão está sem filamento vinculado ou com consumo divergente — estoque pode estar incorreto.'
                                                        : `${productionSummary.filament_pending_jobs} impressões estão sem filamento vinculado ou com consumo divergente — estoque pode estar incorreto.`}
                                                </Box>
                                                {' '}Edite cada uma e informe os carretéis utilizados.
                                            </Alert>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {statsViewMode === 'list' ? (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{ opacity: statsLoading ? 0.5 : 1 }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Taxa de sucesso</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Impressões</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Produtividade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activePrinterSuccessCards.map((printerCard) => (
                                        <PrinterSuccessStatRow
                                            key={printerCard.printer_uuid}
                                            printerCard={printerCard}
                                            highlighted={isPrinterHighlighted(printerCard.printer_uuid)}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Grid container spacing={2} sx={{ opacity: statsLoading ? 0.5 : 1 }}>
                            {activePrinterSuccessCards.map((printerCard) => (
                                <Grid item xs={12} sm={6} md={3} key={printerCard.printer_uuid}>
                                    <PrinterSuccessStatCard
                                        printerCard={printerCard}
                                        printerProgress={printerProgress}
                                        highlighted={isPrinterHighlighted(printerCard.printer_uuid)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {showArchivedPrinters && archivedPrinterSuccessCards.length > 0 && (
                                statsViewMode === 'list' ? (
                                    <TableContainer
                                        component={Paper}
                                        variant="outlined"
                                        sx={{ mt: 2, opacity: statsLoading ? 0.5 : 1 }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Taxa de sucesso</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Impressões</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Produtividade</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {archivedPrinterSuccessCards.map((printerCard) => (
                                                    <PrinterSuccessStatRow
                                                        key={printerCard.printer_uuid}
                                                        printerCard={printerCard}
                                                        highlighted={isPrinterHighlighted(printerCard.printer_uuid)}
                                                    />
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Grid container spacing={2} sx={{ mt: 0.5, opacity: statsLoading ? 0.5 : 1 }}>
                                        {archivedPrinterSuccessCards.map((printerCard) => (
                                            <Grid item xs={12} sm={6} md={3} key={printerCard.printer_uuid}>
                                                <PrinterSuccessStatCard
                                                    printerCard={printerCard}
                                                    printerProgress={printerProgress}
                                                    highlighted={isPrinterHighlighted(printerCard.printer_uuid)}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )
                    )}
                </Box>
            )}

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-printer-label">Impressora</InputLabel>
                            <Select
                                labelId="filter-printer-label"
                                label="Impressora"
                                value={draftPrinter}
                                onChange={(e) => setDraftPrinter(e.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {filterActivePrinters(printers).map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name}
                                    </MenuItem>
                                ))}
                                {printers.filter((printer) => printer.active === false).map((printer) => (
                                    <MenuItem key={printer.id} value={printer.id}>
                                        {printer.name} (arquivada)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="filter-status-label">Status</InputLabel>
                            <Select
                                labelId="filter-status-label"
                                label="Status"
                                value={draftStatus}
                                onChange={(e) => setDraftStatus(e.target.value)}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {JOB_LIST_STATUS_FILTERS.map((s) => (
                                    <MenuItem key={s.value} value={s.value}>
                                        {s.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateFrom}
                            onChange={(e) => handleDraftDateFromChange(e.target.value)}
                            inputProps={{ max: draftDateFromMax || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={draftDateTo}
                            onChange={(e) => handleDraftDateToChange(e.target.value)}
                            inputProps={{
                                min: draftDateToMin || undefined,
                            }}
                            disabled={!draftDateFrom}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            variant="outlined"
                            size="small"
                            fullWidth
                            placeholder="Buscar impressão..."
                            value={draftSearchTerm}
                            onChange={(e) => setDraftSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleApplyFilters();
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleApplyFilters}
                                startIcon={<Search />}
                                fullWidth={isMobile}
                            >
                                Aplicar filtro
                            </Button>
                            {filtersApplied && (
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleClearFilters}
                                    startIcon={<ClearIcon />}
                                    fullWidth={isMobile}
                                >
                                    Limpar
                                </Button>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {!isLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {totalJobs === 1
                        ? '1 impressão encontrada'
                        : `${totalJobs} impressões encontradas`}
                </Typography>
            )}

            {!isLoading && jobs.length === 0 && (
                <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">Nenhuma impressão encontrada.</Typography>
                </Paper>
            )}

            <Stack spacing={1.25}>
                {Array.isArray(jobs) && jobs.length > 0 && jobs.map((job) => {
                    const printer = printersById[job.printer_uuid];
                    const printerLabel = printer?.name || presentUuid(job.printer_uuid);
                    const brandBadge = getPrinterBrandBadgeProps(printer?.brand);
                    const successRate = job.success_percent != null ? `${job.success_percent}%` : '—';
                    const highlighted = isJobHighlighted(job.id) || isJobHighlighted(job.uuid);
                    const filamentPending = Boolean(job.filament_pending);

                    return (
                        <Paper
                            key={job.id}
                            variant="outlined"
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                borderColor: highlighted
                                    ? 'primary.main'
                                    : filamentPending ? 'warning.main' : 'divider',
                                boxShadow: highlighted
                                    ? '0 0 0 1px rgba(25, 118, 210, 0.35)'
                                    : filamentPending ? '0 0 0 1px rgba(237, 108, 2, 0.35)' : 'none',
                                '&:hover': {
                                    borderColor: 'primary.light',
                                    boxShadow: 1,
                                },
                            }}
                        >
                            <Grid container spacing={2} alignItems="flex-start">
                                <Grid item xs={12} md={5}>
                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={700}
                                        sx={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={job.file_name}
                                    >
                                        {job.file_name}
                                    </Typography>
                                    {filamentPending && (
                                        <Chip
                                            size="small"
                                            color="warning"
                                            icon={<WarningAmberOutlined />}
                                            label="Filamento incompleto"
                                            onClick={() => handleEditJob(job.id)}
                                            sx={{ mt: 0.5, mb: 0.5, fontWeight: 600 }}
                                        />
                                    )}
                                    <JobListSpecs job={job} />
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, lineHeight: 1.6 }}>
                                        Início: {formatDate(job.started_at)}
                                        {' · '}
                                        Fim: {formatDate(job.finished_at)}
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={4} md={2}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        Impressora
                                    </Typography>
                                    {brandBadge ? (
                                        <Chip label={printerLabel} size="small" {...brandBadge} />
                                    ) : (
                                        <Typography variant="body2" fontWeight={600} noWrap title={printerLabel}>
                                            {printerLabel}
                                        </Typography>
                                    )}
                                </Grid>

                                <Grid item xs={6} sm={4} md={3}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        Status
                                    </Typography>
                                    <Chip {...getJobStatusBadgeProps(job.status)} />
                                    {job.status === 'em_andamento' && (
                                        <PrinterPrintProgressBar
                                            percent={resolvePrinterPrintProgressPercent(
                                                printerProgress,
                                                job.printer_uuid,
                                            )}
                                            color="info"
                                            sx={{ mt: 1, mb: 0 }}
                                        />
                                    )}
                                    {job.status_reason && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                            sx={{ mt: 0.75, lineHeight: 1.45 }}
                                        >
                                            {job.status_reason}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Objetos: <strong>{job.printed_count}/{job.object_count}</strong>
                                        {' · '}
                                        Sucesso: <strong>{successRate}</strong>
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    md={2}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        gap: 0.5,
                                    }}
                                >
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditJob(job.id)}
                                        aria-label="editar impressão"
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteJob(job.id)}
                                        aria-label="remover impressão"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Paper>
                    );
                })}
            </Stack>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                        setListVersion((v) => v + 1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                        setListVersion((v) => v + 1);
                    }}
                />
            </Box>
        </div>
    );
}

export default ListJobsPage;
